import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Trash2, Eye, ChevronLeft, ChevronRight, X, Save, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { STATUSES, StatusBadge, fmtDate } from "@/pages/admin/adminUi";

// Generic manager for enquiries & appointments.
export default function RecordsManager({ title, endpoint, nameField, columns, detailFields }) {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [date, setDate] = useState("");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search, status, date, sort, page, limit: 10 };
      const r = await api.get(`/admin/${endpoint}`, { params });
      setData(r.data);
    } catch (e) {
      toast.error("Could not load records.");
    } finally { setLoading(false); }
  }, [endpoint, search, status, date, sort, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, status, date, sort]);

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/admin/${endpoint}/${id}`);
      setSelected(r.data);
      setNotes(r.data.internalNotes || "");
    } catch { toast.error("Could not open record."); }
  };

  const changeStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/${endpoint}/${id}`, { status: newStatus });
      toast.success("Status updated");
      setSelected((s) => (s && s.id === id ? { ...s, status: newStatus } : s));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveNotes = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/admin/${endpoint}/${selected.id}`, { internalNotes: notes });
      toast.success("Notes saved");
      setSelected((s) => ({ ...s, internalNotes: notes }));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this record permanently?")) return;
    try {
      await api.delete(`/admin/${endpoint}/${id}`);
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch { toast.error("Delete failed."); }
  };

  const inputCls = "rounded-xl border border-soft-border bg-white px-4 py-2.5 text-sm outline-none focus:border-terracotta";

  return (
    <div data-testid={`admin-${endpoint}`}>
      <h1 className="font-serif text-3xl text-charcoal">{title}</h1>
      <p className="mt-1 text-sm text-taupe">{data.total} total records</p>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-taupe" />
          <input data-testid={`${endpoint}-search`} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, email" className={`${inputCls} w-full pl-10`} />
        </div>
        <select data-testid={`${endpoint}-filter-status`} value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
          <option value="all">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input type="date" data-testid={`${endpoint}-filter-date`} value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
        <select data-testid={`${endpoint}-sort`} value={sort} onChange={(e) => setSort(e.target.value)} className={inputCls}>
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
      </div>

      {/* Table */}
      <div className="mt-6 overflow-x-auto thin-scroll rounded-2xl border border-soft-border bg-cream">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="border-b border-soft-border text-xs uppercase tracking-wide text-taupe">
            <tr>
              {columns.map((c) => <th key={c.key} className="px-5 py-4 font-semibold">{c.label}</th>)}
              <th className="px-5 py-4 font-semibold">Status</th>
              <th className="px-5 py-4 font-semibold">Created</th>
              <th className="px-5 py-4 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-soft-border">
            {loading ? (
              <tr><td colSpan={columns.length + 3} className="px-5 py-10 text-center text-taupe"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={columns.length + 3} className="px-5 py-10 text-center text-taupe">No records found.</td></tr>
            ) : data.items.map((row) => (
              <tr key={row.id} data-testid={`${endpoint}-row`} className="transition-colors hover:bg-blush/40">
                {columns.map((c) => <td key={c.key} className="px-5 py-4 text-charcoal">{c.render ? c.render(row) : (row[c.key] || "—")}</td>)}
                <td className="px-5 py-4"><StatusBadge status={row.status} /></td>
                <td className="px-5 py-4 text-taupe">{fmtDate(row.createdAt)}</td>
                <td className="px-5 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid={`${endpoint}-view`} onClick={() => openDetail(row.id)} className="rounded-lg p-2 text-terracotta hover:bg-blush" aria-label="View"><Eye className="h-4 w-4" /></button>
                    <button onClick={() => remove(row.id)} className="rounded-lg p-2 text-destructive hover:bg-rose-50" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data.pages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-soft-border p-2 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
          <span className="text-sm text-taupe">Page {data.page} of {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-soft-border p-2 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end bg-charcoal/40" onClick={() => setSelected(null)}>
          <div data-testid={`${endpoint}-detail`} className="h-full w-full max-w-md overflow-y-auto bg-background p-6 shadow-lift md:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-serif text-2xl text-charcoal">{selected[nameField]}</h2>
                <p className="mt-1 text-sm text-taupe">Submitted {fmtDate(selected.createdAt)}</p>
              </div>
              <button onClick={() => setSelected(null)} className="rounded-lg p-2 text-taupe hover:bg-blush"><X className="h-5 w-5" /></button>
            </div>

            <div className="mt-6 space-y-4">
              {detailFields.map((f) => (
                <div key={f.key}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-taupe">{f.label}</p>
                  <p className="mt-1 text-sm text-charcoal">{selected[f.key] || "—"}</p>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-taupe">Change Status</p>
              <select data-testid={`${endpoint}-status-select`} value={selected.status} onChange={(e) => changeStatus(selected.id, e.target.value)} className="w-full rounded-xl border border-soft-border bg-white px-4 py-3 text-sm outline-none focus:border-terracotta">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="mt-6">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-taupe">Internal Notes <span className="normal-case text-taupe/70">(admin only)</span></p>
              <textarea data-testid={`${endpoint}-notes`} rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full rounded-xl border border-soft-border bg-white p-4 text-sm outline-none focus:border-terracotta" placeholder="Add private notes…" />
              <button data-testid={`${endpoint}-save-notes`} onClick={saveNotes} disabled={saving} className="mt-3 inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm font-semibold text-cream hover:bg-terracotta-dark disabled:opacity-70">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Notes
              </button>
            </div>

            <button onClick={() => remove(selected.id)} className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-destructive"><Trash2 className="h-4 w-4" /> Delete record</button>
          </div>
        </div>
      )}
    </div>
  );
}
